import React from 'react';
import { observer } from 'owa-mobx-react';
import { ToggleFavoriteButton } from 'owa-favorites';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { isSingleLineListView } from 'owa-mail-layout';
import {
    getIsSearchTableShown,
    isCopilotResultsTable,
    getStore,
    getListViewState,
    type TableView,
} from 'owa-mail-list-store';
import type { UserSettingsStyleSelector } from '../../utils/getStyleSelectorAsPerUserSettings';
import { Tooltip } from '@fluentui/react-components';
import {
    customTextContainer,
    showSelectionModeButtonSCL,
    showSelectionModeButtonMCL,
    full,
    medium,
    compact,
    customHeaderText,
    customHeaderTextWithFavorite,
    customHeaderTextNoMarginBottom,
    favoriteIconFull,
    favoriteIconMedium,
    favoriteIconCompact,
    favoriteIconDenseless,
    copilotResultsTable,
    customHeaderTextTypeRamp,
} from './MailListHeaderSecondRow.scss';
import classnames from 'owa-classnames';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type MailboxInfo from 'owa-client-types/lib/MailboxInfo';

export interface MailListHeaderSecondRowTextContentProps {
    text?: string;
    containerCssClass?: string;
    showFavoriteToggle?: boolean;
    onFavoriteToggleClick?: (evt: React.MouseEvent<unknown>) => void;
    isInFavorites?: boolean;
    styleSelectorAsPerUserSettings?: UserSettingsStyleSelector;
    mailboxInfo: MailboxInfo;
    tableView?: TableView;
    noBottomMargin?: boolean;
}

const noop = () => {};

const defaultExport: React.FunctionComponent<MailListHeaderSecondRowTextContentProps> = observer(
    function MailListHeaderSecondRowTextContent(props: MailListHeaderSecondRowTextContentProps) {
        const isSingleLineView = isSingleLineListView();
        const activeTableView = getStore().tableViews.get(getListViewState().selectedTableViewId);
        const isCopilotResultsTableSelected = isCopilotResultsTable(activeTableView?.tableQuery);
        const showSelectionModeButton = !getIsSearchTableShown() && !isCopilotResultsTableSelected;
        const shouldShowFavoriteToggle = props.showFavoriteToggle;
        const noBottomMargin = props.noBottomMargin ?? false;
        const isTypeRampEnabled = isGlobalFeatureEnabled('mon-type-ramp');

        const containerClassName = classnames(
            customTextContainer,
            props.containerCssClass,
            showSelectionModeButton && !isSingleLineView && showSelectionModeButtonSCL,
            showSelectionModeButton && isSingleLineView && showSelectionModeButtonMCL,
            isCopilotResultsTableSelected && copilotResultsTable,
            getDensityModeCssClass(full, medium, compact)
        );

        return (
            <div className={containerClassName} role="heading" aria-level={2}>
                {props.text && (
                    <Tooltip content={props.text} relationship="label">
                        <span
                            className={classnames(
                                isTypeRampEnabled ? customHeaderTextTypeRamp : customHeaderText,
                                shouldShowFavoriteToggle && customHeaderTextWithFavorite,
                                noBottomMargin && customHeaderTextNoMarginBottom,
                                getDensityModeCssClass(
                                    full,
                                    medium,
                                    isTypeRampEnabled ? medium : compact
                                )
                            )}
                        >
                            {props.text}
                        </span>
                    </Tooltip>
                )}
                {shouldShowFavoriteToggle && (
                    <ToggleFavoriteButton
                        isInFavorites={props.isInFavorites ?? false}
                        onClick={props.onFavoriteToggleClick ?? noop}
                        iconStyles={
                            isTypeRampEnabled
                                ? favoriteIconDenseless
                                : getDensityModeCssClass(
                                      favoriteIconFull,
                                      favoriteIconMedium,
                                      favoriteIconCompact
                                  )
                        }
                        mailboxInfo={props.mailboxInfo}
                    />
                )}
            </div>
        );
    },
    'MailListHeaderSecondRowTextContent'
);
export default defaultExport;
